import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";

import EcoConfirmDialog from "../../../components/eco-ui/EcoConfirmDialog";
import { createAccountApi, deleteAccountApi, listAccountsApi, updateAccountApi } from "../api/financeiroApi";
import { ACCOUNT_TYPE_LABELS, FinAccount, FinAccountType } from "../api/finTypes";
import { formatCurrency, formatDateOnly } from "../../dp/utils/format";
import { parseMoneyInput, todayYmd } from "../utils/fin";

type Props = { token: string; canWrite: boolean };

const TYPES = Object.keys(ACCOUNT_TYPE_LABELS) as FinAccountType[];

const emptyForm = () => ({
  name: "",
  type: "CORRENTE" as FinAccountType,
  bankCode: "",
  bankName: "",
  branch: "",
  accountNumber: "",
  initialBalance: "0",
  initialBalanceAt: todayYmd(),
});

/** Contas bancárias e caixa: saldo inicial numa data e saldo atual derivado das baixas. */
export default function AccountsSettings({ token, canWrite }: Props) {
  const [accounts, setAccounts] = useState<FinAccount[] | null>(null);
  const [editing, setEditing] = useState<FinAccount | "new" | null>(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState<FinAccount | null>(null);

  const load = useCallback(() => {
    listAccountsApi(token, true)
      .then(setAccounts)
      .catch((error: Error) => toast.error(error.message));
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const open = (account?: FinAccount) => {
    setEditing(account ?? "new");
    setForm(
      account
        ? {
            name: account.name,
            type: account.type,
            bankCode: account.bankCode ?? "",
            bankName: account.bankName ?? "",
            branch: account.branch ?? "",
            accountNumber: account.accountNumber ?? "",
            initialBalance: account.initialBalance,
            initialBalanceAt: account.initialBalanceAt,
          }
        : emptyForm()
    );
  };

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    const initialBalance = parseMoneyInput(form.initialBalance || "0");
    if (form.name.trim().length < 2) return toast.error("Informe o nome da conta.");
    if (!Number.isFinite(initialBalance)) return toast.error("Saldo inicial inválido.");
    if (!form.initialBalanceAt) return toast.error("Informe a data do saldo inicial.");

    const payload = {
      name: form.name.trim(),
      type: form.type,
      bankCode: form.bankCode.trim() || null,
      bankName: form.bankName.trim() || null,
      branch: form.branch.trim() || null,
      accountNumber: form.accountNumber.trim() || null,
      initialBalance,
      initialBalanceAt: form.initialBalanceAt,
    };

    setSaving(true);
    try {
      if (editing === "new") await createAccountApi(token, payload);
      else if (editing) await updateAccountApi(token, editing.id, payload);
      toast.success("Conta salva.");
      setEditing(null);
      load();
    } catch (error: any) {
      toast.error(error?.message || "Erro ao salvar a conta.");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (account: FinAccount) => {
    try {
      await updateAccountApi(token, account.id, { isActive: !account.isActive });
      toast.success(account.isActive ? "Conta inativada." : "Conta reativada.");
      load();
    } catch (error: any) {
      toast.error(error?.message || "Erro ao salvar a conta.");
    }
  };

  const remove = async () => {
    if (!removing) return;
    setSaving(true);
    try {
      await deleteAccountApi(token, removing.id);
      toast.success("Conta excluída.");
      setRemoving(null);
      load();
    } catch (error: any) {
      toast.error(error?.message || "Erro ao excluir a conta.");
      setRemoving(null);
    } finally {
      setSaving(false);
    }
  };

  const isCash = form.type === "CAIXA";

  return (
    <div className="dpeSection">
      <div className="dpControls">
        <p className="finHint">
          O saldo atual é o saldo inicial mais as baixas a partir da data dele. Conta com movimento não se exclui: inative.
        </p>
        {canWrite ? (
          <button type="button" className="ecoBtn ecoBtn--primary" onClick={() => open()}>
            Nova conta
          </button>
        ) : null}
      </div>

      <section className="ecoCard">
        <div className="ecoTableWrap">
          <table className="ecoTable finTable">
            <thead>
              <tr>
                <th>Conta</th>
                <th>Tipo</th>
                <th>Banco / agência / conta</th>
                <th className="ecoTable__num">Saldo inicial</th>
                <th className="ecoTable__num">Saldo atual</th>
                {canWrite ? <th className="ecoTable__num">Ações</th> : null}
              </tr>
            </thead>
            <tbody>
              {!accounts ? (
                <tr>
                  <td colSpan={6} className="ecoEmpty">
                    Carregando...
                  </td>
                </tr>
              ) : accounts.length === 0 ? (
                <tr>
                  <td colSpan={6} className="ecoEmpty">
                    Nenhuma conta cadastrada. Cadastre a conta bancária da empresa para registrar pagamentos.
                  </td>
                </tr>
              ) : (
                accounts.map((a) => (
                  <tr key={a.id} className={a.isActive ? "" : "finRow--canceled"}>
                    <td>
                      <strong>{a.name}</strong>
                      {!a.isActive ? <div className="finDesc__sub">Inativa</div> : null}
                    </td>
                    <td>{ACCOUNT_TYPE_LABELS[a.type]}</td>
                    <td className="ecoTable__muted">
                      {[a.bankCode && `${a.bankCode}${a.bankName ? ` ${a.bankName}` : ""}`, a.branch, a.accountNumber]
                        .filter(Boolean)
                        .join(" / ") || "—"}
                    </td>
                    <td className="ecoTable__num finMoney">
                      {formatCurrency(a.initialBalance)}
                      <div className="finDesc__sub">em {formatDateOnly(a.initialBalanceAt)}</div>
                    </td>
                    <td className={`ecoTable__num finMoney ${Number(a.currentBalance) < 0 ? "finMoney--negative" : ""}`}>
                      <strong>{formatCurrency(a.currentBalance)}</strong>
                    </td>
                    {canWrite ? (
                      <td className="ecoTable__num">
                        <div className="dpTable__actions" style={{ justifyContent: "flex-end" }}>
                          <button type="button" className="ecoBtn ecoBtn--sm" onClick={() => open(a)}>
                            Editar
                          </button>
                          <button type="button" className="ecoBtn ecoBtn--sm" onClick={() => toggleActive(a)}>
                            {a.isActive ? "Inativar" : "Reativar"}
                          </button>
                          <button
                            type="button"
                            className="ecoBtn ecoBtn--sm ecoBtn--danger"
                            onClick={() => setRemoving(a)}
                          >
                            Excluir
                          </button>
                        </div>
                      </td>
                    ) : null}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      {editing ? (
        <div className="dpModalOverlay">
          <div className="dpModal" role="dialog" aria-modal="true" aria-label="Conta bancária">
            <header className="dpModal__header">
              <h2 className="dpModal__title">{editing === "new" ? "Nova conta" : "Editar conta"}</h2>
              <button
                type="button"
                className="dpModal__close"
                onClick={() => setEditing(null)}
                disabled={saving}
                aria-label="Fechar"
              >
                &times;
              </button>
            </header>
            <form onSubmit={save}>
              <div className="dpModal__body">
                <div className="dpFormGrid">
                  <div className="dpFormGroup">
                    <label htmlFor="acName">Nome *</label>
                    <input
                      id="acName"
                      className="dpInput"
                      maxLength={80}
                      placeholder="Ex.: Itaú — conta corrente"
                      value={form.name}
                      onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                      disabled={saving}
                    />
                  </div>
                  <div className="dpFormGroup">
                    <label htmlFor="acType">Tipo *</label>
                    <select
                      id="acType"
                      className="dpSelect"
                      value={form.type}
                      onChange={(e) => setForm((f) => ({ ...f, type: e.target.value as FinAccountType }))}
                      disabled={saving}
                    >
                      {TYPES.map((t) => (
                        <option key={t} value={t}>
                          {ACCOUNT_TYPE_LABELS[t]}
                        </option>
                      ))}
                    </select>
                  </div>
                  {!isCash ? (
                    <>
                      <div className="dpFormGroup">
                        <label htmlFor="acBank">Banco (código)</label>
                        <input
                          id="acBank"
                          className="dpInput"
                          maxLength={5}
                          placeholder="341"
                          value={form.bankCode}
                          onChange={(e) => setForm((f) => ({ ...f, bankCode: e.target.value }))}
                          disabled={saving}
                        />
                      </div>
                      <div className="dpFormGroup">
                        <label htmlFor="acBankName">Banco (nome)</label>
                        <input
                          id="acBankName"
                          className="dpInput"
                          maxLength={80}
                          value={form.bankName}
                          onChange={(e) => setForm((f) => ({ ...f, bankName: e.target.value }))}
                          disabled={saving}
                        />
                      </div>
                      <div className="dpFormGroup">
                        <label htmlFor="acBranch">Agência</label>
                        <input
                          id="acBranch"
                          className="dpInput"
                          maxLength={10}
                          value={form.branch}
                          onChange={(e) => setForm((f) => ({ ...f, branch: e.target.value }))}
                          disabled={saving}
                        />
                      </div>
                      <div className="dpFormGroup">
                        <label htmlFor="acNumber">Conta</label>
                        <input
                          id="acNumber"
                          className="dpInput"
                          maxLength={20}
                          value={form.accountNumber}
                          onChange={(e) => setForm((f) => ({ ...f, accountNumber: e.target.value }))}
                          disabled={saving}
                        />
                      </div>
                    </>
                  ) : null}
                  <div className="dpFormGroup">
                    <label htmlFor="acInitial">Saldo inicial (R$)</label>
                    <input
                      id="acInitial"
                      className="dpInput"
                      inputMode="decimal"
                      value={form.initialBalance}
                      onChange={(e) => setForm((f) => ({ ...f, initialBalance: e.target.value }))}
                      disabled={saving}
                    />
                    <span className="dpFormHint">Pode ser negativo (cheque especial).</span>
                  </div>
                  <div className="dpFormGroup">
                    <label htmlFor="acInitialAt">Saldo em *</label>
                    <input
                      id="acInitialAt"
                      type="date"
                      className="dpInput"
                      value={form.initialBalanceAt}
                      onChange={(e) => setForm((f) => ({ ...f, initialBalanceAt: e.target.value }))}
                      disabled={saving}
                    />
                    <span className="dpFormHint">Baixas antes desta data não mexem no saldo.</span>
                  </div>
                </div>
              </div>
              <footer className="dpModal__footer">
                <button type="button" className="dpBtn dpBtn--secondary" onClick={() => setEditing(null)} disabled={saving}>
                  Cancelar
                </button>
                <button type="submit" className="dpBtn dpBtn--primary" disabled={saving}>
                  {saving ? "Salvando..." : "Salvar"}
                </button>
              </footer>
            </form>
          </div>
        </div>
      ) : null}

      <EcoConfirmDialog
        open={removing !== null}
        title="Excluir conta?"
        message={`"${removing?.name ?? ""}" será excluída. Só é possível em conta sem nenhuma baixa.`}
        confirmLabel="Excluir"
        tone="danger"
        busy={saving}
        onConfirm={remove}
        onCancel={() => setRemoving(null)}
      />
    </div>
  );
}
